import React, { useMemo } from 'react';
import { RefreshCw } from 'lucide-react';

export interface TimelineDataPoint {
  time: string;
  timestamp: number;
  value: number;  // 1 = active (有人/開/移動), 0 = inactive (無人/關/靜止)
  label?: string;
}

export interface OccupancyTimelineProps {
  data: TimelineDataPoint[];
  loading?: boolean;
  title?: string;
  // Labels for active/inactive states
  activeLabel?: string;    // e.g., "有人", "開啟", "移動"
  inactiveLabel?: string;  // e.g., "無人", "關閉", "靜止"
  // Colors for active/inactive states
  activeColor?: string;    // e.g., "#22c55e" (green)
  inactiveColor?: string;  // e.g., "#374151" (dark gray)
  // Whether to show a legend
  showLegend?: boolean;
}

/**
 * OccupancyTimeline Style 01 - Timeline with Statistics
 * 
 * Displays:
 * 1. Duration statistics for active/inactive states (time + percentage)
 * 2. Horizontal timeline bar showing state changes over 24H
 */
export const Style01_Timeline: React.FC<OccupancyTimelineProps> = ({
  data,
  loading = false,
  title = '過去 24 小時佔用統計',
  activeLabel = '有人',
  inactiveLabel = '無人',
  activeColor = '#22c55e',
  inactiveColor = '#374151',
  showLegend = true,
}) => {
  // Calculate statistics from data
  const stats = useMemo(() => {
    if (!data || data.length === 0) {
      return { activeMs: 0, inactiveMs: 0, totalMs: 24 * 60 * 60 * 1000, segments: [] };
    }

    // Sort data by timestamp
    const sortedData = [...data].sort((a, b) => a.timestamp - b.timestamp);
    
    // Calculate total time range (use 24 hours if data spans less)
    const now = Date.now();
    const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000;
    const startTime = Math.max(sortedData[0]?.timestamp || twentyFourHoursAgo, twentyFourHoursAgo);
    const endTime = now;
    const totalMs = endTime - startTime;

    let activeMs = 0;
    let inactiveMs = 0;
    const segments: Array<{ start: number; end: number; active: boolean }> = [];

    // Process each data point to calculate durations
    for (let i = 0; i < sortedData.length; i++) {
      const current = sortedData[i];
      const next = sortedData[i + 1];
      const segmentStart = Math.max(current.timestamp, startTime);
      const segmentEnd = next ? Math.min(next.timestamp, endTime) : endTime;
      const duration = segmentEnd - segmentStart;

      if (duration > 0) {
        const isActive = current.value === 1 || current.value > 0;
        if (isActive) {
          activeMs += duration;
        } else {
          inactiveMs += duration;
        }
        segments.push({
          start: (segmentStart - startTime) / totalMs * 100,
          end: (segmentEnd - startTime) / totalMs * 100,
          active: isActive,
        });
      }
    }

    // If no segments, calculate based on last known state or assume inactive
    if (segments.length === 0 && sortedData.length > 0) {
      const lastState = sortedData[sortedData.length - 1];
      if (lastState.value === 1 || lastState.value > 0) {
        activeMs = totalMs;
      } else {
        inactiveMs = totalMs;
      }
    } else if (segments.length === 0) {
      inactiveMs = totalMs;
    }

    return { activeMs, inactiveMs, totalMs, segments, startTime, endTime };
  }, [data]);

  // Format duration as "X小時 Y分"
  const formatDuration = (ms: number): string => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    if (hours === 0) {
      return `${minutes}分`;
    }
    return `${hours}小時 ${minutes}分`;
  };

  // Format time for labels
  const formatTimeLabel = (timestamp: number): string => {
    const date = new Date(timestamp);
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const period = hours >= 12 ? '下午' : '上午';
    const displayHours = hours % 12 || 12;
    return `${period}${displayHours}:${minutes}`;
  };

  // Calculate percentages
  const activePercent = stats.totalMs > 0 ? (stats.activeMs / stats.totalMs * 100) : 0;
  const inactivePercent = stats.totalMs > 0 ? (stats.inactiveMs / stats.totalMs * 100) : 0;

  // Generate time axis labels (5 labels: start, 25%, 50%, 75%, end)
  const timeLabels = useMemo(() => {
    if (!stats.startTime || !stats.endTime) return [];
    const labels: Array<{ position: number; label: string }> = [];
    const timeRange = stats.endTime - stats.startTime;
    
    // 5 equally spaced labels
    for (let i = 0; i <= 4; i++) {
      const timestamp = stats.startTime + (timeRange * i / 4);
      labels.push({
        position: i * 25,
        label: i === 4 ? '現在' : formatTimeLabel(timestamp),
      });
    }
    return labels;
  }, [stats.startTime, stats.endTime]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-slate-300">{title}</h4>
          <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
        </div>
        <div className="flex items-center justify-center h-32">
          <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Title */}
      <h4 className="text-sm font-medium text-slate-300">{title}</h4>

      {/* Statistics Cards */}
      <div className="flex gap-4">
        {/* Active State */}
        <div className="flex-1 bg-slate-800/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div 
              className="w-2.5 h-2.5 rounded-full" 
              style={{ backgroundColor: activeColor }}
            />
            <span className="text-sm text-slate-400">{activeLabel}</span>
          </div>
          <div className="text-xl font-bold text-white">
            {formatDuration(stats.activeMs)}
          </div>
          <div className="text-sm text-slate-500">
            {activePercent.toFixed(1)}%
          </div>
        </div>

        {/* Inactive State */}
        <div className="flex-1 bg-slate-800/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div 
              className="w-2.5 h-2.5 rounded-full" 
              style={{ backgroundColor: inactiveColor }}
            />
            <span className="text-sm text-slate-400">{inactiveLabel}</span>
          </div>
          <div className="text-xl font-bold text-white">
            {formatDuration(stats.inactiveMs)}
          </div>
          <div className="text-sm text-slate-500">
            {inactivePercent.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Timeline Bar */}
      <div className="space-y-2">
        <div className="text-xs text-slate-500">時間軸</div>
        <div 
          className="relative h-4 rounded-full overflow-hidden"
          style={{ backgroundColor: inactiveColor }}
        >
          {/* Render segments */}
          {stats.segments.map((segment, index) => (
            <div
              key={index}
              className="absolute top-0 h-full"
              style={{
                left: `${segment.start}%`,
                width: `${segment.end - segment.start}%`,
                backgroundColor: segment.active ? activeColor : inactiveColor,
              }}
            />
          ))}
        </div>

        {/* Time Axis Labels */}
        <div className="relative h-4">
          {timeLabels.map((item, index) => (
            <div
              key={index}
              className="absolute text-xs text-slate-500 transform -translate-x-1/2"
              style={{ left: `${item.position}%` }}
            >
              {item.label}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      {showLegend && (
        <div className="flex items-center justify-center gap-6 text-xs text-slate-400">
          <div className="flex items-center gap-1.5">
            <div 
              className="w-3 h-2 rounded-sm" 
              style={{ backgroundColor: activeColor }}
            />
            <span>{activeLabel}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div 
              className="w-3 h-2 rounded-sm" 
              style={{ backgroundColor: inactiveColor }}
            />
            <span>{inactiveLabel}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Style01_Timeline;
